
import { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
  AddressElement
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface StripeCheckoutFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  creatorId: string;
  subscriptionType: 'monthly' | 'lifetime';
  amount: number;
  username:string;
}

export const StripeCheckoutForm = ({
  onSuccess,
  onCancel,
  creatorId,
  subscriptionType,
  amount,
  username
}: StripeCheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Build return URL with required parameters
      const returnUrl = new URL(`${window.location.origin}/payment-confirmation`);
      returnUrl.searchParams.append('creator_id', creatorId);
      returnUrl.searchParams.append('creator_name', username);
      returnUrl.searchParams.append('type', subscriptionType);
      returnUrl.searchParams.append('amount', amount.toString());

      console.log('Confirming payment with parameters:', {
        creatorId,
        type: subscriptionType,
        amount,
        returnUrl: returnUrl.toString(),
        timestamp: new Date().toISOString()
      });

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl.toString(),
          payment_method_data: {
            billing_details: {
              // AddressElement will automatically collect and populate this
            }
          }
        },
        redirect: "always"
      });

      if (error) {
        setErrorMessage(error.message);
        toast.error(error.message || "Payment failed");
        console.error('Payment confirmation error:', error);
      } else {
        toast.success("Payment successful!");
        onSuccess();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed");
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6">
      <div className="space-y-4">
        {/* Billing Address Collection */}
        <div className="rounded-md border p-4">
          <h3 className="text-sm font-medium mb-2">Billing Address</h3>
          <AddressElement 
            options={{
              mode: 'billing',
              allowedCountries: ['US']
            }} 
          />
        </div>

        {/* Payment Method Selection */}
        <div className="rounded-md border p-4">
          <h3 className="text-sm font-medium mb-2">Payment Method</h3>
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card', 'us_bank_account']
            }}
          />
        </div>
      </div>

      {errorMessage && (
        <div className="text-sm text-red-600 mt-2">{errorMessage}</div>
      )}
      
      <div className="flex gap-4 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Pay now'
          )}
        </Button>
      </div>
    </form>
  );
};
